import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';

// ========================================
// Types for News Service
// ========================================

export interface NewsSource {
  id: string;
  name: string;
  domain: string;
}

export interface NewsContent {
  summary: string;
  originalText?: string;
  source: string;
  sourceUrl?: string;
  category?: string;
  publishedDate?: string;
  keyPoints: string[];
}

export interface NewsSummary {
  lesson_id: string;  // Using lesson_id from database
  lesson_name: string;  // Title
  lesson_objective: string;  // Description
  category: string;  // Will be 'news-vietnamese'
  lesson_content: NewsContent;
  created_at: string;
  updated_at: string;
}

export interface NewsSummaryListItem {
  lesson_id: string;
  lesson_name: string;
  lesson_objective: string;
  category: string;
  created_at: string;
  updated_at: string;
}

// Vietnamese news sources
export const NEWS_SOURCES: NewsSource[] = [
  { id: 'tuoitre', name: 'Tuổi Trẻ', domain: 'tuoitre.vn' },
  { id: 'cafef', name: 'CafeF', domain: 'cafef.vn' },
  { id: 'cafebiz', name: 'CafeBiz', domain: 'cafebiz.vn' },
  { id: 'dantri', name: 'Dân Trí', domain: 'dantri.com.vn' },
  { id: 'vnexpress', name: 'VnExpress', domain: 'vnexpress.vn' },
  { id: 'plo', name: 'Pháp Luật Online', domain: 'plo.vn' }
];

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// ========================================
// News Service Class
// ========================================

export class NewsService {
  /**
   * Generate Vietnamese news summary using AI
   * @param selectedSources Array of source IDs to search
   * @param keywords Optional keywords to focus on
   * @param onProgress Optional callback for progress tracking
   */
  public static async generateNewsSummary(
    selectedSources: string[],
    keywords?: string,
    onProgress?: (stage: 'searching' | 'summarizing') => void
  ): Promise<NewsSummary> {
    try {
      console.log('📰 [NewsService] Generating news summary...', {
        sources: selectedSources,
        keywords: keywords || 'none'
      });

      if (onProgress) onProgress('searching');

      const result = await apiCall('/news/generate', {
        method: 'POST',
        body: JSON.stringify({
          selectedSources,
          keywords: keywords || undefined
        })
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to generate news summary');
      }

      console.log('📰 [NewsService] News summary generated successfully');

      // Transform database format to service format
      const newsSummary: NewsSummary = {
        lesson_id: result.data.lesson_id,
        lesson_name: result.data.lesson_name,
        lesson_objective: result.data.lesson_objective,
        category: result.data.category,
        lesson_content: result.data.lesson_content,
        created_at: result.data.created_at,
        updated_at: result.data.updated_at
      };

      return newsSummary;
    } catch (error) {
      console.error('📰 [NewsService] Error generating news summary:', error);
      
      // Handle authentication errors
      if (error instanceof Error) {
        if (error.message.includes('not authenticated')) {
          throw new Error('Please sign in to generate news summaries.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Get past news summaries for user
   */
  public static async getPastSummaries(limit: number = 50, offset: number = 0): Promise<{
    summaries: NewsSummaryListItem[];
    total: number;
  }> {
    try {
      const result = await apiCall(`/news?limit=${limit}&offset=${offset}&sortBy=created_at&order=desc`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch news summaries');
      }

      return {
        summaries: result.data.summaries,
        total: result.data.total
      };
    } catch (error) {
      console.error('📰 [NewsService] Error fetching past summaries:', error);
      throw error;
    }
  }

  /**
   * Get specific news summary by ID
   */
  public static async getSummaryById(newsId: string): Promise<NewsSummary> {
    try {
      const result = await apiCall(`/news/${newsId}`);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch news summary');
      }

      // Transform database format to service format
      const newsSummary: NewsSummary = {
        lesson_id: result.data.lesson_id,
        lesson_name: result.data.lesson_name,
        lesson_objective: result.data.lesson_objective,
        category: result.data.category,
        lesson_content: result.data.lesson_content,
        created_at: result.data.created_at,
        updated_at: result.data.updated_at
      };

      return newsSummary;
    } catch (error) {
      console.error('📰 [NewsService] Error fetching news summary:', error);
      throw error;
    }
  }

  /**
   * Delete news summary
   */
  public static async deleteSummary(newsId: string): Promise<void> {
    try {
      const result = await apiCall(`/news/${newsId}`, {
        method: 'DELETE'
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete news summary');
      }
    } catch (error) {
      console.error('📰 [NewsService] Error deleting news summary:', error);
      throw error;
    }
  }

  /**
   * Get source name by domain
   */
  public static getSourceName(domain: string): string {
    const source = NEWS_SOURCES.find(s => s.domain === domain);
    return source ? source.name : domain;
  }

  /**
   * Get source ID by domain
   */
  public static getSourceId(domain: string): string {
    const source = NEWS_SOURCES.find(s => s.domain === domain);
    return source ? source.id : 'unknown';
  }
}

